import { useEffect, useRef, useState } from 'react';
import { Box, Dialog, InputAdornment, TextField, Typography, useTheme } from '@mui/material';

import CommonButton from '../CommonButton';
import { useWeb3 } from '@/hooks/use-web3';
import {
  CALCULATEPENDINGREWARDFORSTAKE,
  CLAIMREWARD,
  COIN_NAME_SFL,
  POLYGON_AMOY,
  POLYGON_MAIN,
  POLYGON_SHUFFLE,
  PoolState,
  RES_OVER_BLOCK,
  RES_SUCCESS,
  RES_SUCCESS_201,
  STATE,
} from '@/lib/common/name-space';
import { CreateRewardUser, isCompleteModalType } from '@/types';
import SymbolSvg from '@/assets/assetfi/Symbol';
import CloseSvg from '@/assets/assetfi/Close';
import UsdtBalance from '@/component/balance/UsdtBalance';
import { claimRewardUsers } from '@/api/staking/create';
import { ContractInfoType } from '@/types/staking/read';
import Loading from '@/component/mui/Backdrop';
import { converterToEth } from '@/lib/web3/utils';
import { useNavigate } from 'react-router-dom';
import { addLocalSetStorageArr } from '@/lib/storage';
import ResultPopup from '@/lib/ResultPopup';
import { STATE_STAKE } from '@/lib/common/name-space/status';
import { useWeb3AuthConnect } from '@web3auth/modal/react';
import { setTicker } from '@/component/balance/ticker';
import BigNumber from 'bignumber.js';

interface ClaimModalPropsType {
  handleClose: () => void;
  open: boolean;
  contractInfo: ContractInfoType;
  setDoneClaim: (value: boolean) => void;
}

const ClaimModal = ({ handleClose, open, contractInfo, setDoneClaim }: ClaimModalPropsType) => {
  const state = useRef('');
  const [isLoading, setIsLoading] = useState(false);
  // state
  const [isCompleteModal, setIsCompleteModal] = useState<isCompleteModalType>({
    success: false,
    fail: false,
  });
  const [errorMsg, setErrorMsg] = useState('');
  const address = useRef('');
  const [amt, setAmt] = useState({
    stake: '0',
    reward: '0',
    pending: false,
  });
  const [usdt, setUsdt] = useState('0');

  const theme = useTheme();
  const { isConnected } = useWeb3AuthConnect();
  const { web3authCall, address: addr } = useWeb3();
  const [isRetry, setIsRetry] = useState(false); // 50블록 넘었을 때 다시 시도
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      if (open) {
        setUsdt(await setTicker({ navigate }));
      }
    })();
  }, [open]);

  useEffect(() => {
    if (open && isConnected && addr && contractInfo.contractAddr && web3authCall) {
      (async () => {
        address.current = addr;
        state.current = await setPoolState();
        // // 상태 정보
        // setDetail(await setDetails());
        // 사용자가 스테이킹한 금액
        // const stakeAmt = await web3authCall.callStaking(GETTOTALSTAKEDAMOUNT, contractInfo.contractAddr, [addr]);
        // console.log(`stakeAmt`);
        // console.log(stakeAmt);

        // 모금 기간에 호출 불가
        if (
          Number(state.current) === PoolState.POOLSTATE_OPERATING ||
          Number(state.current) === PoolState.POOLSTATE_OPERATING_CLOSED ||
          Number(state.current) === PoolState.POOLSTATE_OPERATING_STOPPED
        ) {
          const rewardAmt = await web3authCall.callStaking(CALCULATEPENDINGREWARDFORSTAKE, contractInfo.contractAddr, [
            addr,
            contractInfo.index,
          ]);
          funcSetAmt({ reward: converterToEth(rewardAmt.reward, 'wei'), stake: contractInfo.stakeAmt });
        } else {
          funcSetAmt('stake', contractInfo.stakeAmt);
        }
      })();
    }

    return () => {};
  }, [web3authCall, contractInfo, open, isConnected, addr]);

  // 50블록 안에 채택이 되지 않을 시 다시 시도할 때
  useEffect(() => {
    if (isRetry) {
      (async () => {
        await handleConfirm();
        setIsRetry(false);
      })();
    }

    return () => {};
  }, [isRetry]);

  // method
  const funcSetAmt = (key: string | Partial<typeof amt>, value?: string) => {
    if (typeof key === 'string' && value !== undefined) {
      setAmt((prev) => ({
        ...prev,
        [key]: value,
      }));
    } else if (typeof key === 'object') {
      setAmt((prev) => ({
        ...prev,
        ...key,
      }));
    }
  };
  const handleConfirm = async () => {
    // 실제 클래임 컨트랙트 발생
    handleClose();
    setIsLoading(true);
    let claim: any;
    let apiSuccess = false;

    try {
      // 1. 컨트랙트 클레임 호출
      claim = await claimIndexReward();
      if (claim.code === RES_OVER_BLOCK) {
        triggerIsCompleteModal('pending');
        setErrorMsg(claim.msg);
        return;
      } else if (claim.code !== RES_SUCCESS) {
        triggerIsCompleteModal('fail');
        setErrorMsg(claim.msg);
        return;
      }

      // 2. API 호출
      const obj: CreateRewardUser = {
        poolId: contractInfo.poolId,
        joinId: contractInfo.joinId ?? null,
        address: address.current,
        rewardAmt: claim.data.contractReward ?? amt?.reward,
        rewardHash: claim.data.transactionHash,
        rewardCnt: contractInfo?.cnt || 1,
      };
      console.log('claimRewardUsers 요청:', obj);
      const result = await claimRewardUsers(obj, false);
      console.log('claimRewardUsers 응답:', result);

      // API 응답 상태 확인
      if (result?.statusCode === RES_SUCCESS || result?.statusCode === RES_SUCCESS_201) {
        apiSuccess = true;
      }
    } catch (e: any) {
      console.error('claimRewardUsers 에러:', e);

      // 에러 메시지 추출
      const errorMessage =
        e?.response?.data?.message ||
        e?.message ||
        e?.msg ||
        (typeof e === 'string' ? e : '리워드 저장 중 오류가 발생했습니다.');

      setErrorMsg(errorMessage);

      // 로컬 스토리지에 저장 (나중에 재시도용)
      try {
        if (claim?.data) {
          const obj: CreateRewardUser = {
            poolId: contractInfo.poolId,
            joinId: contractInfo.joinId ?? null,
            address: address.current,
            rewardAmt: claim.data.contractReward ?? amt?.reward,
            rewardHash: claim.data.transactionHash,
            rewardCnt: contractInfo?.cnt || 1,
          };
          addLocalSetStorageArr('reward', obj);
          console.log('로컬 스토리지에 저장됨 (재시도용):', obj);
        }
      } catch (storageError) {
        console.error('로컬 스토리지 저장 실패:', storageError);
      }

      triggerIsCompleteModal('fail');
    } finally {
      // 항상 로딩 종료
      setIsLoading(false);

      // API 호출이 성공한 경우에만 성공 모달 표시
      if (apiSuccess) {
        triggerIsCompleteModal('success');
        setTimeout(() => {
          setDoneClaim(true);
        }, 2000);
      }
    }
  };
  const claimIndexReward = async () => {
    const re = await web3authCall!.callStaking(CLAIMREWARD, contractInfo.contractAddr, [
      address.current,
      contractInfo.index,
    ]);
    return re;
  };

  const triggerIsCompleteModal = (key: keyof isCompleteModalType) => {
    setIsCompleteModal((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };
  const setPoolState = async (): Promise<string> => {
    return await web3authCall?.callStaking(STATE, contractInfo.contractAddr);
  };
  // TODO
  // 시세 가져오기

  return (
    <>
      <Dialog
        sx={{
          '& .MuiDialog-paper': {
            width: '320px',
            padding: '24px 0px',
            borderRadius: '20px',
          },
        }}
        open={open}
      >
        <Box
          sx={{
            display: 'flex',
            gap: '10px',
            justifyContent: 'space-between',
            padding: '0px 24px',
          }}
        >
          <SymbolSvg width={24} height={24} />
          {/* <Typography color={theme.greyscale.darkgrey2} variant="h6">
            RWA Staking Hupayx No.1
            {detail?.name}
          </Typography> */}

          <Box onClick={handleClose} sx={{ cursor: 'pointer' }}>
            <CloseSvg width={24} height={24} />
          </Box>
        </Box>

        <Box sx={{ marginTop: '24px', padding: '0px 16px', position: 'relative' }}>
          <Typography sx={{ marginLeft: '8px' }} variant="subtitle2" color={theme.greyscale.grey3}>
            Stake Amount
          </Typography>

          <Typography
            sx={{ position: 'absolute', right: 24, bottom: 28 }}
            color={theme.greyscale.grey3}
            variant="body2"
          >
            ~ {BigNumber(amt.stake).multipliedBy(usdt).toFixed()} USD
          </Typography>
          <TextField
            value={amt?.stake}
            disabled
            sx={{
              width: '100%',
              marginTop: '8px',

              '& .MuiOutlinedInput-root': {
                borderRadius: '10px',
                '& input': {
                  textAlign: 'right',
                  typography: 'h4',
                  color: theme.greyscale.darkgrey2,
                  padding: '8px',
                  paddingRight: '0px',
                  paddingBottom: '28px',
                },
                '& fieldset': {
                  borderColor: theme.background.blue,
                },
                '&:hover fieldset': {
                  borderColor: theme.background.blue,
                },
                '&.Mui-focused fieldset': {
                  borderColor: theme.background.blue,
                },
                '&.MuiInputBase-root': {
                  background: '#357AFF1A',
                  padding: 0,
                },
                '& .MuiTypography-root': {
                  typography: 'h5',
                  color: theme.greyscale.darkgrey2,
                },
              },
            }}
            variant="outlined"
            InputProps={{
              endAdornment: (
                <InputAdornment
                  sx={{
                    fontSize: '24px',
                    paddingRight: '8px',
                    paddingBottom: '20px',
                  }}
                  position="end"
                >
                  SFL
                </InputAdornment>
              ),
            }}
          />
          <Typography sx={{ marginRight: '8px' }} align="right" variant="subtitle2" color={theme.greyscale.grey3}>
            Balance ~{' '}
            <UsdtBalance
              symbol={COIN_NAME_SFL}
              chainId={process.env.NODE_ENV === 'production' ? POLYGON_MAIN : POLYGON_AMOY}
              token={POLYGON_SHUFFLE}
            />
          </Typography>
        </Box>

        <Box sx={{ marginTop: '24px', padding: '0px 16px' }}>
          <Typography sx={{ marginLeft: '8px' }} variant="subtitle2" color={theme.greyscale.grey3}>
            Details
          </Typography>

          <Box
            sx={{
              marginTop: '8px',
              background: theme.background.default,
              borderRadius: '10px',
              border: `0.5px solid ${theme.greyscale.grey1}`,
            }}
          >
            {/* <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '8px',
                borderBottom: `0.5px solid ${theme.greyscale.lightgrey2}`,
              }}
            >
              <Typography variant="body2" color={theme.greyscale.grey1}>
                No.
              </Typography>

              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="subtitle2" color={theme.greyscale.darkgrey2}>
                  1
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '8px',
                borderBottom: `0.5px solid ${theme.greyscale.lightgrey2}`,
              }}
            >
              <Typography variant="body2" color={theme.greyscale.grey1}>
                Stake Amount
              </Typography>

              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="subtitle2" color={theme.greyscale.darkgrey2}>
                  1,000,000{' '}
                  <Typography display={'inline'} variant="body4" color={theme.greyscale.darkgrey2}>
                    USD
                  </Typography>
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '8px',
                borderBottom: `0.5px solid ${theme.greyscale.lightgrey2}`,
              }}
            >
              <Typography variant="body2" color={theme.greyscale.grey1}>
                APR
              </Typography>

              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="subtitle2" color={theme.greyscale.darkgrey2}>
                  10.0%
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '8px',
                borderBottom: `0.5px solid ${theme.greyscale.lightgrey2}`,
              }}
            >
              <Typography variant="body2" color={theme.greyscale.grey1}>
                Pool End
              </Typography>

              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="subtitle2" color={theme.greyscale.darkgrey2}>
                  MM-DD-YYYY 00:00
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '8px',
                borderBottom: `0.5px solid ${theme.greyscale.lightgrey2}`,
              }}
            >
              <Typography variant="body2" color={theme.greyscale.grey1}>
                Payment Date
              </Typography>

              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="subtitle2" color={theme.greyscale.darkgrey2}>
                  MM-DD-YYYY 00:00
                </Typography>
              </Box>
            </Box> */}

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '8px',
              }}
            >
              <Typography variant="body2" color={theme.greyscale.grey1}>
                Rewards
              </Typography>

              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="subtitle2" color={theme.greyscale.darkgrey2}>
                  {amt?.reward} SFL
                  {/* 1,000 USD */}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            marginTop: '24px',
            padding: '0px 16px',
            paddingTop: '24px',
            borderTop: `0.5px solid ${theme.greyscale.lightgrey2}`,
          }}
        >
          <CommonButton
            sx={{ width: '100%', padding: '12px 20px', borderRadius: '10px' }}
            text="Claim"
            handleClick={handleConfirm}
            variant="h6"
            color={'#ffffff'}
            bgColor={theme.background.blue}
            border={theme.background.blue}
          />
          <CommonButton
            sx={{
              width: '100%',
              padding: '12px 20px',
              borderRadius: '10px',
              marginTop: '10px',
            }}
            text="Back"
            handleClick={handleClose}
            variant="h6"
            color={theme.greyscale.lightgrey2}
            border={theme.greyscale.lightgrey2}
            bgColor={'#ffffff'}
          />
        </Box>
      </Dialog>

      <ResultPopup
        isCompleteModal={isCompleteModal}
        triggerIsCompleteModal={triggerIsCompleteModal}
        errorMsg={errorMsg}
        type={STATE_STAKE.CLAIM}
        setIsRetry={setIsRetry}
      />

      <Loading isLoading={isLoading} />
    </>
  );
};

export default ClaimModal;
